import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { FaExclamationTriangle, FaRedo, FaDesktop, FaSync, FaCheckCircle, FaExclamationCircle, FaLink, FaTimes, FaTimesCircle, FaInfoCircle, FaCog, FaShieldAlt } from 'react-icons/fa';
import '../../../styles/EndpointSecurityStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * Endpoint Security Dashboard - Tactical RMM Integration
 * Displays on-premise endpoint telemetry and security metrics
 * Provides unified visibility across cloud (Defender/Intune) and on-premise (TRMM) endpoints
 */
const EndpointSecurityDashboard = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [agents, setAgents] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [correlation, setCorrelation] = useState(null);
    const [daysBack, setDaysBack] = useState(7);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [agentDetails, setAgentDetails] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const storedUser = user || JSON.parse(localStorage.getItem('user'));
        return storedUser?.token ? { Authorization: `Bearer ${storedUser.token}` } : {};
    };

    /**
     * Load TRMM agents
     */
    const loadAgents = async () => {
        try {
            const response = await fetch('/api/TacticalRMM/agents', { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load agents');
            const data = await response.json();
            setAgents(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading agents:', err);
            throw err;
        }
    };

    /**
     * Load security alerts
     */
    const loadAlerts = async (days) => {
        try {
            const response = await fetch(`/api/TacticalRMM/alerts?daysBack=${days}`, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load alerts');
            const data = await response.json();
            setAlerts(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading alerts:', err);
            throw err;
        }
    };

    /**
     * Load security metrics
     */
    const loadMetrics = async (days) => {
        try {
            const response = await fetch(`/api/TacticalRMM/metrics?daysBack=${days}`, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load metrics');
            const data = await response.json();
            setMetrics(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading metrics:', err);
            throw err;
        }
    };

    /**
     * Load correlation data (TRMM vs Defender/Intune)
     */
    const loadCorrelation = async () => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch('/api/TacticalRMM/correlate', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });
            if (!response.ok) throw new Error('Failed to load correlation');
            const data = await response.json();
            setCorrelation(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading correlation:', err);
            // Non-critical, continue without correlation
        }
    };

    /**
     * Load agent details
     */
    const loadAgentDetails = async (agentId) => {
        try {
            const response = await fetch(`/api/TacticalRMM/agents/${agentId}`, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load agent details');
            const data = await response.json();
            setAgentDetails(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading agent details:', err);
            setAgentDetails(null);
        }
    };

    /**
     * Initial data load
     */
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    loadAgents(),
                    loadAlerts(daysBack),
                    loadMetrics(daysBack),
                    loadCorrelation()
                ]);
                setError(null);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    /**
     * Reload data when daysBack changes
     */
    useEffect(() => {
        if (!loading) {
            loadAlerts(daysBack);
            loadMetrics(daysBack);
        }
    }, [daysBack]);

    /**
     * Refresh all data
     */
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                loadAgents(),
                loadAlerts(daysBack),
                loadMetrics(daysBack),
                loadCorrelation()
            ]);
            if (selectedAgent) {
                await loadAgentDetails(selectedAgent.agentId);
            }
            setError(null);
        } catch (catchError) {
            Sentry.captureException(catchError);
            setError('Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    /**
     * Handle agent selection
     */
    const handleAgentSelect = async (agent) => {
        setSelectedAgent(agent);
        await loadAgentDetails(agent.agentId);
    };

    /**
     * Get status badge class
     */
    const getStatusClass = (status) => {
        if (!status) return 'offline';
        const s = status.toLowerCase();
        if (s === 'online') return 'online';
        if (s === 'offline') return 'offline';
        if (s === 'overdue') return 'overdue';
        return 'unknown';
    };

    /**
     * Get alert severity class
     */
    const getAlertSeverityClass = (severity) => {
        if (!severity) return '';
        const s = severity.toLowerCase();
        if (s === 'error') return 'error';
        if (s === 'warning') return 'warning';
        return 'info';
    };

    if (loading) {
        return (
            <div className="endpoint-security-dashboard">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading endpoint security data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="endpoint-security-dashboard">
                <div className="error-message">
                    <FaExclamationTriangle />
                    <p>{error}</p>
                    <button onClick={handleRefresh} className="btn-retry">
                        <FaRedo /> Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="endpoint-security-dashboard">
            <div className="dashboard-header">
                <h1>
                    <FaDesktop /> Endpoint Security Monitoring
                </h1>
                <p className="subtitle">Tactical RMM On-Premise Endpoint Telemetry</p>
                <div className="header-controls">
                    <select 
                        value={daysBack} 
                        onChange={(e) => setDaysBack(parseInt(e.target.value))}
                        className="days-selector"
                    >
                        <option value="7">Last 7 days</option>
                        <option value="14">Last 14 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="60">Last 60 days</option>
                        <option value="90">Last 90 days</option>
                    </select>
                    <button 
                        onClick={handleRefresh} 
                        className="btn-refresh"
                        disabled={refreshing}
                    >
                        <FaSync className={refreshing ? 'spinning' : ''} />
                        {refreshing ? ' Refreshing...' : ' Refresh'}
                    </button>
                </div>
            </div>

            {/* Security Metrics Summary */}
            {metrics && (
                <div className="security-summary">
                    <h2>Endpoint Security Overview</h2>
                    <div className="summary-cards">
                        <div className="summary-card">
                            <div className="card-icon"><FaDesktop /></div>
                            <div className="card-content">
                                <div className="card-value">{metrics.totalAgents}</div>
                                <div className="card-label">Total Endpoints</div>
                            </div>
                        </div>
                        <div className="summary-card online">
                            <div className="card-icon"><FaCheckCircle /></div>
                            <div className="card-content">
                                <div className="card-value">{metrics.onlineAgents}</div>
                                <div className="card-label">Online</div>
                            </div>
                        </div>
                        <div className="summary-card warning">
                            <div className="card-icon"><FaExclamationCircle /></div>
                            <div className="card-content">
                                <div className="card-value">{metrics.offlineAgents}</div>
                                <div className="card-label">Offline</div>
                            </div>
                        </div>
                        <div className="summary-card error">
                            <div className="card-icon"><FaExclamationTriangle /></div>
                            <div className="card-content">
                                <div className="card-value">{metrics.overdueAgents}</div>
                                <div className="card-label">Overdue</div>
                            </div>
                        </div>
                    </div>

                    <div className="alert-summary">
                        <h3>Alert Summary</h3>
                        <div className="alert-cards">
                            <div className="alert-card error">
                                <div className="alert-count">{metrics.errorAlerts}</div>
                                <div className="alert-label">Errors</div>
                            </div>
                            <div className="alert-card warning">
                                <div className="alert-count">{metrics.warningAlerts}</div>
                                <div className="alert-label">Warnings</div>
                            </div>
                            <div className="alert-card info">
                                <div className="alert-count">{metrics.infoAlerts}</div>
                                <div className="alert-label">Info</div>
                            </div>
                        </div>
                    </div>

                    {metrics.agentsOfflineOver24Hours > 0 && (
                        <div className="risk-indicator">
                            <FaExclamationTriangle />
                            <span>{metrics.agentsOfflineOver24Hours} endpoint(s) offline for more than 24 hours</span>
                        </div>
                    )}
                </div>
            )}

            {/* Cross-Platform Correlation */}
            {correlation && correlation.securityGaps && correlation.securityGaps.length > 0 && (
                <div className="correlation-section">
                    <h2><FaLink /> Security Coverage Analysis</h2>
                    <div className="correlation-warning">
                        <FaExclamationTriangle />
                        <span>
                            {correlation.securityGaps.length} endpoint(s) present in Tactical RMM 
                            but not managed by Defender or Intune
                        </span>
                    </div>
                    <div className="gap-list">
                        {correlation.securityGaps.map((gap, index) => (
                            <div key={index} className="gap-item">
                                <FaDesktop />
                                <span>{gap.hostname}</span>
                                <small>({gap.operatingSystem})</small>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Agents List */}
            <div className="agents-section">
                <h2>Managed Endpoints ({agents.length})</h2>
                <div className="agents-list">
                    {agents.map((agent) => (
                        <div 
                            key={agent.agentId}
                            className={`agent-item ${getStatusClass(agent.status)}`}
                            onClick={() => handleAgentSelect(agent)}
                        >
                            <div className="agent-icon">
                                <FaDesktop />
                            </div>
                            <div className="agent-info">
                                <div className="agent-name">{agent.hostname}</div>
                                <div className="agent-os">{agent.operatingSystem}</div>
                            </div>
                            <div className={`agent-status status-${getStatusClass(agent.status)}`}>
                                {agent.status}
                            </div>
                        </div>
                    ))}
                </div>

                {agents.length === 0 && (
                    <div className="no-data">
                        <FaDesktop />
                        <p>No endpoints found in Tactical RMM</p>
                        <small>Check TRMM configuration in Settings &gt; App Secrets</small>
                    </div>
                )}
            </div>

            {/* Recent Alerts */}
            {alerts && alerts.length > 0 && (
                <div className="alerts-section">
                    <h2>Recent Security Alerts</h2>
                    <div className="alerts-list">
                        {alerts.slice(0, 10).map((alert, index) => (
                            <div key={index} className={`alert-item ${getAlertSeverityClass(alert.severity)}`}>
                                <div className="alert-icon">
                                    {alert.severity === 'error' ? <FaTimesCircle /> : alert.severity === 'warning' ? <FaExclamationTriangle /> : <FaInfoCircle />}
                                </div>
                                <div className="alert-content">
                                    <div className="alert-message">{alert.message}</div>
                                    <div className="alert-meta">
                                        <span className="alert-agent">{alert.agentHostname}</span>
                                        <span className="alert-time">{new Date(alert.timestamp).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Selected Agent Details */}
            {selectedAgent && agentDetails && (
                <div className="agent-details-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{agentDetails.hostname}</h3>
                            <button onClick={() => setSelectedAgent(null)} className="btn-close">
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-section">
                                <h4>System Information</h4>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <span className="label">Operating System:</span>
                                        <span className="value">{agentDetails.operatingSystem}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">RAM:</span>
                                        <span className="value">{agentDetails.totalRam} GB</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Status:</span>
                                        <span className={`value status-${getStatusClass(agentDetails.status)}`}>
                                            {agentDetails.status}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Last Seen:</span>
                                        <span className="value">{new Date(agentDetails.lastSeen).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            {agentDetails.services && agentDetails.services.length > 0 && (
                                <div className="detail-section">
                                    <h4>Critical Services ({agentDetails.services.length})</h4>
                                    <div className="services-list">
                                        {agentDetails.services.slice(0, 5).map((service, index) => (
                                            <div key={index} className="service-item">
                                                <span>{service.name}</span>
                                                <span className={`service-status ${service.status}`}>{service.status}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Help Section */}
            <div className="help-section">
                <h3><FaInfoCircle /> About Endpoint Security Monitoring</h3>
                <p>
                    This dashboard provides comprehensive monitoring of on-premise endpoints managed by Tactical RMM.
                    Monitor agent status, view security alerts, and identify coverage gaps between TRMM and cloud
                    security platforms (Defender/Intune).
                </p>
                <div className="help-links">
                    <a href="/settings/secrets" className="help-link">
                        <FaCog /> Configure TRMM Settings
                    </a>
                    <a href="/dashboards/threat-hunting" className="help-link">
                        <FaShieldAlt /> View Threat Hunting Report
                    </a>
                </div>
            </div>
        </div>
    );
};

export default EndpointSecurityDashboard;
